<template>
  <div v-if="canShow">
    <div v-if="updatingTour" class="loader-tour-container">
      <v-loader />
    </div>

    <template v-if="status === 'loaded'">
      <div class="w-100 d-flex justify-content-start mb-8 ms-12">
        <v-button
          :disabled="updatingTour || isSold"
          class="btn-add-plus rounded-circle shadow-sm"
          size="xs"
          variant="primary"
          @click="addTourDayAtFirst()"
        >
          <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
        </v-button>
      </div>

      <div class="tour-container">
        <the-case-day-draggable
          v-model="tourDays"
          :agency="agency.slug"
          :client-tour="tourMapped.clientTour"
          :current-tour-day="currentTourDayId"
          :end-date="tourMapped.clientTour.end_date"
          :init-date="tourMapped.clientTour.start_date"
          :tour-id="tourMapped.id"
          :tour-mapped="tourMappedOriginal"
          :updating-tour="updatingTour"
          :is-sold="isSold"
          class="mb-16"
          type="tour-day"
          @day-moved="updateTourDay"
          @emit-add-day="addTourDay"
          @reload-tour="loadTour"
          @reload-client-tour="$emit('reloadClientTour')"
          @open-item-details-off-canvas="openItemDetailsOffCanvas"
          @close-item-details-off-canvas="
            closeOffCanvas();
            clearCurrentTourDayItem();
          "
          @open-day-information-off-canvas="openDayInformationOffCanvas"
          @move-item-to-another-day="moveItemToAnotherDay"
        />
      </div>

      <div class="w-100 d-flex justify-content-center mt-8">
        <v-button
          :disabled="updatingTour || isSold"
          class="btn-add-plus rounded-circle shadow-sm"
          size="xs"
          variant="primary"
          @click="addTourDayAtEnd()"
        >
          <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
        </v-button>
      </div>
    </template>

    <v-loader v-else />

    <the-tour-day-item-details-off-canvas-v2
      v-if="offCanvasToShow === 'the-tour-day-item-details-off-canvas'"
      :agency="agency.slug"
      :days-count="daysCount"
      :client-tour-id="tourMapped.id"
      :status="statusTourDayItem"
      :tour-id="tourMapped.id"
      :is-sold="isSold"
      @closed="
        closeOffCanvas();
        clearCurrentTourDayItem();
      "
      @emit-reload-tour="loadTour"
      @open-item-details-off-canvas="openItemDetailsOffCanvas"
    />

    <the-tour-day-information-off-canvas-v2
      v-if="offCanvasToShow === 'the-tour-day-information-off-canvas'"
      :agency="agency.slug"
      :client-tour-id="tourMapped.id"
      :tour-day-id="currentTourDayId"
      :tour-id="tourMapped.id"
      @closed="
        closeOffCanvas();
        currentTourDayId = null;
      "
      @emit-reload-tour="loadTour"
    />

    <the-confirmation-modal
      v-if="showConfirmationModal"
      :title="$t('tourClient.connectLuggage')"
      @confirm="doConnectLuggage"
      @closed="closeConfirmModal"
      :btn-loading="updatingTour"
    />
  </div>

  <the-screen-size-verify @can-show="canShow = $event" />
</template>

<script lang="ts" setup name="TheCaseBuilder">
import _ from "lodash";
import { DateTime } from "luxon";
import { storeToRefs } from "pinia";
import { TYPE, useToast } from "vue-toastification";
import api from "@/api";
import TheCaseDayDraggable from "@/components/case/V2/TheCaseDayDraggableV2.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAuthStore } from "@/stores/auth";
import TheTourDayItemDetailsOffCanvasV2 from "@/components/tour/V2/TheTourDayItemDetailsOffCanvasV2.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import { useUserStore } from "@/stores/user";
import TheTourDayInformationOffCanvasV2 from "@/components/tour/V2/TheTourDayInformationOffCanvasV2.vue";
import TheScreenSizeVerify from "@/components/vendor/TheScreenSizeVerify.vue";
import TheConfirmationModal from "@/components/modal/TheConfirmationModal.vue";
import { ref } from "vue";
import { onMounted } from "vue";
import { watch } from "vue";
import { getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

const props = defineProps<{
  caseId: string;
  tourId: string;
}>();

const emit = defineEmits<{
  (e: "reloadClientTour"): void;
}>();

const userStore = useUserStore();
const authStore = useAuthStore();
const { agency } = storeToRefs(authStore);
const isSold = computed(() => userStore.caseIsSold);
const { openOffCanvas, offCanvasToShow, closeOffCanvas } = useOffCanvasUtils();

const canShow = ref(false);
const statusTourDayItem = ref("loading");
const statusTourDay = ref("loading");
const tourMapped = ref<Record<string, any>>({});
const tourMappedOriginal = ref<Record<string, any>>({});
const tourDays = ref<Record<string, any>[]>([]);
const status = ref("loading");
const updatingTour = ref(false);
const templateName = ref("");
const currentTourDayId = ref<number | null>(null);
const showConfirmationModal = ref(false);
const daysCount = ref(0);

const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;

watch(
  () => props.tourId,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await loadTour();
    }
  }
);

onMounted(async () => {
  await loadTour();
});

const loadTour = async () => {
  updatingTour.value = true;

  if (agency.value) {
    const caseBuilder: Record<string, any> = await api.case.caseBuilderDetails(agency.value.slug, Number(props.caseId));

    templateName.value = caseBuilder.name;
    mapTour(caseBuilder.content);
  }

  updatingTour.value = false;
};

const mapCaseDayItem = (
  item: Record<string, any>,
  disabled = false,
  supplierName = "-"
) => ({
  id: item.id,
  ...item,
  disabled,
  supplier: supplierName,
});

const mapTour = (caseBuilder: Record<string, any>) => {
  const tourDaysItemsHotelCache: Record<string, any>[] = [];

  daysCount.value = caseBuilder.case_days.length;

  const sortedCaseDays = _.orderBy(caseBuilder.case_days || [], ["order"], ["asc"]);

  const mappedTourDays = sortedCaseDays.map((caseDay: any) => {
    const items: Record<string, any>[] = [];

    if (Array.isArray(caseDay.case_day_items)) {
      caseDay.case_day_items.forEach((caseDayItem: any) => {
        const supplierName =
          caseDayItem?.item_data?.name || "-";

        const mappedItem = mapCaseDayItem(
          caseDayItem,
          false,
          supplierName
        );

        items.push(mappedItem);
      });
    }

    if (Array.isArray(caseDay.disabledItems)) {
      caseDay.disabledItems.forEach((disabledItem: any) => {
        const supplierName =
          disabledItem?.item_data?.name || "-";

        items.push(
          mapCaseDayItem(disabledItem, true, supplierName)
        );
      });
    }

    const sortedItems = _.orderBy(items, ["position"], ["asc"]);
    const stageLength = caseDay.stageLength ?? caseDay.stage_length ?? 0;

    return {
      id: caseDay.id,
      ...caseDay,
      stageLength,
      stage_length: caseDay.stage_length ?? caseDay.stageLength ?? stageLength,
      isBlocked: false,
      items: sortedItems,
    };
  });

  tourDaysItemsHotelCache.forEach((hotelItem) => {
    const matchingDay = mappedTourDays.find(
      (day: any) =>
        DateTime.fromISO(day.date).toFormat("yyyy-MM-dd") ===
        hotelItem.dateCheckout
    );

    if (matchingDay) {
      matchingDay.items.unshift({
        ...hotelItem,
        disabled: true,
      });
    }
  });

  tourMapped.value = {
    id: caseBuilder.id,
    ...caseBuilder,
    tourDays: mappedTourDays,
    client: caseBuilder.client,
    clientTour: {
      start_date: caseBuilder.start_date,
      end_date: caseBuilder.end_date,
    },
  };

  tourDays.value = mappedTourDays;
  tourMappedOriginal.value = _.cloneDeep(tourMapped.value);
  status.value = "loaded";
};

const moveItemToAnotherDay = async (item: Record<string, any>) => {
  const newCaseDay = _.find(tourDays.value, (tourDay: Record<string, any>) =>
    _.find(tourDay.items, (tourDayItem: Record<string, any>) => tourDayItem.id === item.element.id)
  );

  if (!!agency.value && !!newCaseDay) {
    try {
      await api.case.caseDayItemMoveAnotherDay(
        agency.value.slug,
        tourMapped.value.id,
        item.oldElement.case_day_id,
        item.element.id,
        { day_id: newCaseDay.id }
      );

      toast.success(t("general.shared.savedChanges"));
      await loadTour();
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Error");
      await loadTour();
    }
  }
};

const updateTourDay = async (caseDay: Record<string, any>, order: string | number) => {
  if (!!agency.value) {
    await api.case.caseDayUpdateOrder(agency.value.slug, caseDay.case_id, caseDay.id, {
      order,
    });
  }

  await loadTour();
};

const addTourDay = async (order: string) => {
  await $modal?.confirm({
    title: t("tourBuilder.addDayTitle"),
    text: t("tourBuilder.addDayText"),
    leftButtonText: t("general.button.cancel"),
    switchButtons: true,
    confirmButtonCallback: async () => {
      if (!!agency.value) {
        const loadingToast = $toast?.loading(t("general.button.adding"), "toast-add-tour-day");

        try {
          await api.case.caseDayCreate(agency.value.slug, tourMapped.value.id, {
            order,
          });

          await loadTour();

          loadingToast?.update("toast-add-tour-day", {
            content: t("tourBuilder.dayCreated"),
            options: {
              type: TYPE.SUCCESS,
              icon: "icon icon-check-circle-fill",
              timeout: 5000,
            },
          });
        } catch (e: any) {
          loadingToast?.update("toast-add-tour-day", {
            content: t("tourBuilder.dayCreateError"),
            options: {
              type: TYPE.ERROR,
              icon: "icon icon-exclamation-triangle-fill",
              timeout: 5000,
            },
          });

          console.error(e.response?.data);
          toast.error(e.response?.data?.message || "An error occurred");
        }
      }
    },
    confirmButtonLoadingText: t("general.button.adding"),
    confirmButtonText: t("general.button.add"),
  });
};

const addTourDayAtFirst = async () => {
  await $modal?.confirm({
    title: t("tourBuilder.addDayTitle"),
    text: t("tourBuilder.addDayText"),
    leftButtonText: t("general.button.cancel"),
    switchButtons: true,
    confirmButtonCallback: async () => {
      if (agency.value) {
        const loadingToast = $toast?.loading(t("general.button.adding"), "toast-add-tour-day-at-first");
        try {
          await api.case.caseDayCreate(agency.value.slug, tourMapped.value.id, {
            order: 1,
          });

          await loadTour();
          emit("reloadClientTour");

          loadingToast?.update("toast-add-tour-day-at-first", {
            content: t("tourBuilder.dayCreated"),
            options: {
              type: TYPE.SUCCESS,
              icon: "icon icon-check-circle-fill",
              timeout: 5000,
            },
          });
        } catch (e: any) {
          loadingToast?.update("toast-add-tour-day-at-first", {
            content: t("tourBuilder.dayCreateError"),
            options: {
              type: TYPE.ERROR,
              icon: "icon icon-exclamation-triangle-fill",
              timeout: 5000,
            },
          });

          console.error(e?.response?.data);
          toast.error(e?.response?.data?.message);
        }
      }
    },
    confirmButtonLoadingText: t("general.button.adding"),
    confirmButtonText: t("general.button.add"),
  });
};

const addTourDayAtEnd = async () => {
  await $modal?.confirm({
    title: t("tourBuilder.addDayTitle"),
    text: t("tourBuilder.addDayText"),
    leftButtonText: t("general.button.cancel"),
    switchButtons: true,
    confirmButtonCallback: async () => {
      if (!!agency.value) {
        const loadingToast = $toast?.loading(t("general.button.adding"), "toast-add-tour-day-at-end");

        try {
          await api.case.caseDayCreate(agency.value.slug, tourMapped.value.id, {
            order: tourMapped.value.case_days.length + 1,
          });

          await loadTour();
          emit("reloadClientTour");

          loadingToast?.update("toast-add-tour-day-at-end", {
            content: t("tourBuilder.dayCreated"),
            options: {
              type: TYPE.SUCCESS,
              icon: "icon icon-check-circle-fill",
              timeout: 5000,
            },
          });
        } catch (e) {
          loadingToast?.update("toast-add-tour-day-at-end", {
            content: t("tourBuilder.dayCreateError"),
            options: {
              type: TYPE.ERROR,
              icon: "icon icon-exclamation-triangle-fill",
              timeout: 5000,
            },
          });

          console.error(e.response?.data);
          toast.error(e.response?.data?.message);
        }
      }
    },
    confirmButtonLoadingText: t("general.button.adding"),
    confirmButtonText: t("general.button.add"),
  });
};

const openItemDetailsOffCanvas = async (data: any) => {
  statusTourDayItem.value = "loading";

  if (offCanvasToShow.value !== "the-tour-day-item-details-off-canvas") {
    openOffCanvas("the-tour-day-item-details-off-canvas");
  }

  await userStore.setCurrentCaseDayItem(data);
  statusTourDayItem.value = "loaded";
};

const openDayInformationOffCanvas = async (tourDayId: any) => {
  currentTourDayId.value = tourDayId;

  if (offCanvasToShow.value !== "the-tour-day-information-off-canvas") {
    openOffCanvas("the-tour-day-information-off-canvas");
  }
};

const clearCurrentTourDayItem = () => {
  userStore.setCurrentTourDayItem(null);
};

const closeConfirmModal = () => {
  showConfirmationModal.value = false;
};
</script>

<style lang="scss" scoped>
.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.tour-steps-container {
  gap: 1rem;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: absolute;
}

.list-group-item {
  display: block;
  padding: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  border-radius: 0;
  width: 100%;
}
</style>
